import type {
  CloudEnvironmentCatalogRow,
  CloudProjectCatalogRow,
} from "../../cmmd/cmmdForgeRuntimeListResource";
import type { EnvironmentId } from "@cmmd-forge/contracts";
import type { CmmdForgeRuntimeLifecycleAction } from "../../environments/primary/cmmdForgeRuntimeApi";
import { Trash2Icon, WrenchIcon } from "lucide-react";
import { Separator } from "../ui/separator";
import { deriveCloudProjectKey } from "../sidebar/cloudProjectsGroup.logic";
import { RowActionIconButton } from "../ui/row-action-icon-button";
import {
  projectCloudEnvironmentLifecycle,
  withShellConnectionProof,
} from "../../environments/primary/cloudEnvironmentLifecycleProjection";
import { CloudRuntimeRowActionsCell } from "./cloudRuntimeRowActionsCell";
import { REMOVE_CHECKOUT_LABEL, REPAIR_LABEL } from "./cloudRuntimeRowActions";
import {
  CloudEnvironmentNameActions,
  CloudEnvironmentNameField,
  CloudEnvironmentNameTrigger,
} from "./cloudEnvironmentName";
import {
  EXECUTION_TARGET_ROW_CLASSNAME,
  ExecutionTargetActions,
  ExecutionTargetIdentity,
  ITEM_ROW_INNER_CLASSNAME,
} from "./executionTargetRow.shared";
import { useCloudEnvironmentRename } from "./useCloudEnvironmentRename";
import { useEnvironmentApi } from "~/environmentApi";

type CloudRuntimeLifecycleProjection = ReturnType<typeof withShellConnectionProof>;

function resolveEnvironmentNote(
  environment: CloudEnvironmentCatalogRow,
  lifecycle: CloudRuntimeLifecycleProjection,
): string | null {
  if (environment.maintenance?.verdict === "reconciliation_required") {
    return "Server version needs reconciliation";
  }
  if (environment.maintenance?.verdict === "drifted") return "Server update available";
  return lifecycle.detail;
}

function CloudEnvironmentRowIdentity({
  environment,
  environmentName,
  lifecycle,
  rename,
}: {
  readonly environment: CloudEnvironmentCatalogRow;
  readonly environmentName: string;
  readonly lifecycle: CloudRuntimeLifecycleProjection;
  readonly rename: ReturnType<typeof useCloudEnvironmentRename>;
}) {
  return rename.isEditing ? (
    <CloudEnvironmentNameField derivedName={environmentName} rename={rename} />
  ) : (
    <ExecutionTargetIdentity
      tone={lifecycle.tone}
      name={<CloudEnvironmentNameTrigger name={environmentName} onStart={rename.start} />}
      statusLabel={lifecycle.label}
      accessibleStatus={`Environment ${lifecycle.label}`}
      meta={[]}
      note={resolveEnvironmentNote(environment, lifecycle)}
    />
  );
}

function CloudEnvironmentRowActions({
  environment,
  lifecycle,
  rename,
  isBusy,
  busyActionLabel,
  rows,
  onDeleteEnvironment,
  onRecover,
  onUpdate,
  onRunLifecycleAction,
}: {
  readonly environment: CloudEnvironmentCatalogRow;
  readonly lifecycle: CloudRuntimeLifecycleProjection;
  readonly rename: ReturnType<typeof useCloudEnvironmentRename>;
  readonly isBusy: boolean;
  readonly busyActionLabel: string | null;
  readonly rows: readonly CloudProjectCatalogRow[];
  readonly onDeleteEnvironment: (
    environment: CloudEnvironmentCatalogRow,
    rows: readonly CloudProjectCatalogRow[],
  ) => void;
  readonly onRecover: (
    environment: CloudEnvironmentCatalogRow,
    rows: readonly CloudProjectCatalogRow[],
  ) => void;
  readonly onUpdate: (environment: CloudEnvironmentCatalogRow) => void;
  readonly onRunLifecycleAction: (
    row: CloudProjectCatalogRow | CloudEnvironmentCatalogRow,
    action: CmmdForgeRuntimeLifecycleAction,
  ) => void;
}) {
  return rename.isEditing ? (
    <CloudEnvironmentNameActions rename={rename} />
  ) : (
    <CloudRuntimeRowActionsCell
      environment={environment}
      lifecycle={lifecycle}
      isBusy={isBusy}
      busyActionLabel={busyActionLabel}
      onDeleteEnvironment={() => onDeleteEnvironment(environment, rows)}
      handlers={{
        onUpdate,
        onRunLifecycleAction,
        onRecoverEnvironment: () => onRecover(environment, rows),
      }}
    />
  );
}

export function CloudRuntimeExecutionRow({
  environmentName,
  environment,
  rows,
  busyCloudProjectKey,
  busyCloudActionLabel,
  onRepair,
  onRecover,
  onDeleteEnvironment,
  onRemoveProject,
  onUpdate,
  onRunLifecycleAction,
  onRenamed,
}: {
  readonly environmentName: string;
  readonly environment: CloudEnvironmentCatalogRow;
  readonly rows: readonly CloudProjectCatalogRow[];
  readonly busyCloudProjectKey: string | null;
  readonly busyCloudActionLabel: string | null;
  readonly onRepair: (row: CloudProjectCatalogRow) => void;
  readonly onRecover: (
    environment: CloudEnvironmentCatalogRow,
    rows: readonly CloudProjectCatalogRow[],
  ) => void;
  readonly onDeleteEnvironment: (
    environment: CloudEnvironmentCatalogRow,
    rows: readonly CloudProjectCatalogRow[],
  ) => void;
  readonly onRemoveProject: (row: CloudProjectCatalogRow) => void;
  readonly onUpdate: (environment: CloudEnvironmentCatalogRow) => void;
  readonly onRunLifecycleAction: (
    row: CloudProjectCatalogRow | CloudEnvironmentCatalogRow,
    action: CmmdForgeRuntimeLifecycleAction,
  ) => void;
  /** Refetch the snapshot, so the row and the delete gate move together. */
  readonly onRenamed: () => void;
}) {
  const rename = useCloudEnvironmentRename({
    runtimeId: environment.runtimeId,
    displayName: environment.displayName,
    onRenamed,
  });
  const isBusy = busyCloudProjectKey === environment.runtimeId;
  const shellApi = useEnvironmentApi(
    (environment.cmmdEnvironmentId ?? environment.environmentId) as EnvironmentId,
  );
  const lifecycle = withShellConnectionProof(
    environment.lifecycle ?? projectCloudEnvironmentLifecycle(undefined),
    shellApi !== undefined,
  );
  return (
    <div className={`group ${EXECUTION_TARGET_ROW_CLASSNAME}`} data-slot="execution-target-row">
      <div className={ITEM_ROW_INNER_CLASSNAME} data-slot="execution-target-row-grid">
        <CloudEnvironmentRowIdentity
          environment={environment}
          environmentName={environmentName}
          lifecycle={lifecycle}
          rename={rename}
        />
        <ExecutionTargetActions>
          <CloudEnvironmentRowActions
            environment={environment}
            lifecycle={lifecycle}
            rename={rename}
            isBusy={isBusy}
            busyActionLabel={busyCloudActionLabel}
            rows={rows}
            onDeleteEnvironment={onDeleteEnvironment}
            onRecover={onRecover}
            onUpdate={onUpdate}
            onRunLifecycleAction={onRunLifecycleAction}
          />
        </ExecutionTargetActions>
      </div>
      <div className="ml-4 border-l border-border pl-3 sm:ml-5 sm:pl-4">
        {rows.map((project) => (
          <CloudEnvironmentProjectRow
            key={deriveCloudProjectKey(project)}
            project={project}
            isBusy={busyCloudProjectKey === deriveCloudProjectKey(project)}
            onRepair={onRepair}
            onRemoveProject={onRemoveProject}
          />
        ))}
      </div>
    </div>
  );
}

function CloudEnvironmentProjectRow({
  project,
  isBusy,
  onRepair,
  onRemoveProject,
}: {
  readonly project: CloudProjectCatalogRow;
  readonly isBusy: boolean;
  readonly onRepair: (row: CloudProjectCatalogRow) => void;
  readonly onRemoveProject: (row: CloudProjectCatalogRow) => void;
}) {
  return (
    <div
      className="flex min-h-touch items-center justify-between gap-3 py-2 md:min-h-7"
      data-slot="cloud-checkout-row"
    >
      <p className="min-w-0 truncate text-sm text-foreground">{project.projectName}</p>
      <div className="flex shrink-0 items-center gap-2">
        <RowActionIconButton
          label={REPAIR_LABEL}
          icon={<WrenchIcon aria-hidden="true" className="size-3.5" />}
          disabled={isBusy}
          onClick={() => onRepair(project)}
        />
        <Separator orientation="vertical" className="h-5" />
        <RowActionIconButton
          label={REMOVE_CHECKOUT_LABEL}
          icon={<Trash2Icon aria-hidden="true" className="size-3.5" />}
          variant="destructive-outline"
          disabled={isBusy}
          onClick={() => onRemoveProject(project)}
        />
      </div>
    </div>
  );
}
